import { Grid, PerspectiveCamera } from '@react-three/drei';
import { Suspense, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';

import type { CharacterDefinition, CharacterPoseLibrary } from '../content/schema/character';
import type { MapDocument } from '../content/schema/map';
import type { AssetRuntimeState } from '../content/registry/objectRenderers';
import { MapEditorOverlay } from '../editor/MapEditorOverlay';
import type { SceneObjectRoomResolver } from '../editor/components/ObjectListSidebar';
import { DEFAULT_BLOOM_INTENSITY, DEFAULT_VIGNETTE_AMOUNT, useEngineRuntimeStore } from '../store/engineRuntimeStore';
import { useMapEditorStore } from '../editor/store/mapEditorStore';
import type { MapFloorLevel, MapTriggerVolume } from '../content/schema/map';
import { registerTriggerHandler, unregisterTriggerHandler } from '../content/registry/triggerRegistry';
import {
  FirstPersonController,
  PlayerAvatar,
  type CameraOverrideTarget,
  type PlayerAvatarCustomBody,
  type PlayerCallbacks,
  type PlayerConfig,
  type PlayerMotionState,
  type TeleportRequest,
} from './FirstPersonController';
import { LevelRenderer } from './LevelRenderer';
import { resolveIdlePose, resolveWalkPose } from './PlacedCharacters';
import { PostEffects } from './PostEffects';
import { Platformer2DScene } from './twod/Platformer2DScene';
import { DEFAULT_PLATFORMER_CONFIG, type PlatformerConfig } from './twod/platformer2d';
import { Tilemap2DScene, type Tilemap2DFrameInfo } from './twod/Tilemap2DScene';
import { TouchControlsOverlay } from './touch/TouchControls';
import { deriveMapData, floorWorldY, type MapRuntimeOptions } from './mapRuntime';
import { resolveDropPlacement } from './dropPlacement';
import { ClientLogicRuntime, type ClientLogicRuntimeHandle } from './logic/clientLogicRuntime';
import { resetLiveSceneStore, useLiveSceneStore } from './liveSceneStore';
import type { LogicDocument } from './logic/contract';
import type { LogicEffect } from './logic/evaluator';
import { Viewport } from './viewport/Viewport';
import { CutscenePlayer, type CutsceneActor } from './CutscenePlayer';
import type { Cutscene } from '../content/schema/cutscene';
import { tilemap2DTriggersToMapTriggerVolumes } from './logic/triggerEvents';
import { RemotePlayers } from './multiplayer/RemotePlayers';
import { RemotePlayers2D } from './multiplayer/RemotePlayers2D';
import type { Interpolator, InterpolatedTransform } from './multiplayer/interpolator';

export interface SceneTheme {
  /** Clear/background colour. */
  background: string;
  /** Linear fog. */
  fog?: { color: string; near: number; far: number };
  /** Ambient fill. */
  ambient: { color?: string; intensity: number };
  /** Hemisphere fill (sky/ground). */
  hemisphere?: { sky: string; ground: string; intensity: number };
  /** Key directional light. */
  directional?: {
    color?: string;
    intensity: number;
    position: [number, number, number];
    castShadow?: boolean;
  };
  /** Enable the post-processing stack (bloom/vignette/grain/chromatic). */
  postEffects?: boolean;
  /** Bloom intensity (0-1); defaults to the engine baseline when omitted. */
  bloom?: number;
  /** Vignette darkness (0-1); defaults to the engine baseline when omitted. */
  vignette?: number;
}

export interface GameRuntimeConfig {
  theme: SceneTheme;
  /** First-person controller tuning overrides. */
  player?: Partial<PlayerConfig>;
  /** Floor-height options shared by geometry + walkability. */
  runtime?: MapRuntimeOptions;
}

export interface GameRuntimeProps {
  mapDocument: MapDocument;
  config: GameRuntimeConfig;
  /**
   * Character definitions available to the project. Resolved against
   * `mapDocument.characterInstances`' `characterId` so a placed character
   * (with or without a patrol) actually renders (see `LevelRenderer`'s
   * `PlacedCharacters`); also used by edit tools.
   */
  characters?: CharacterDefinition[];
  /** Pose libraries for `characters` (a placed character's idle/walk pose). */
  poseLibraries?: CharacterPoseLibrary[];
  mode: 'play' | 'edit';
  /**
   * Game dimension. '2D' renders the map document's tilemap2d layer
   * orthographically with top-down movement; '3D' (default) renders the
   * level. A 2D projection without a tilemap2d layer falls back to 3D.
   */
  projection?: '2D' | '3D';
  /**
   * Edit-mode chrome: 'full' renders the complete MapEditorOverlay panels;
   * 'viewport' renders only the canvas (interactions and drop placement stay
   * live) so a host can supply its own editor chrome around it.
   */
  chrome?: 'full' | 'viewport';
  /** Editor sidebar grouping (edit mode). */
  roomResolver?: SceneObjectRoomResolver;
  /** Editor sidebar heading (edit mode). */
  editorTitle?: string;
  /** Opaque per-object runtime bag forwarded to renderers (play mode). */
  runtimeState?: AssetRuntimeState;
  /** Extra controller callbacks for a hosting game (audio, footsteps, etc.). */
  callbacks?: Partial<PlayerCallbacks>;
  /**
   * Per-frame player position on the ground plane, both projections: 3D
   * reports world metres (x, y, z); 2D reports tile coordinates as (x, 0, y).
   * Drives host overlays (minimap) and positional audio.
   */
  onPlayerPosition?: (x: number, y: number, z: number) => void;
  /**
   * Per-frame local player transform, play mode only - `x/y/z` in the same
   * per-projection convention as `onPlayerPosition` (3D world metres; 2D
   * tile coordinates as `x, 0, y`), plus `yaw` (radians), which
   * `onPlayerPosition` has no room for. Exists for the multiplayer sync
   * engine's transform broadcaster (docs/multiplayer-sync/roadmap.md
   * section 3.1) - a host reads this to report the local player's own
   * transform over the relay at a throttled interval, gated on an active
   * relay connection; unused, this is a harmless extra per-frame callback,
   * the same "additive, no-op until wired" posture presence already has. 2D
   * top-down derives yaw from movement direction (no rotation concept for a
   * flat sprite otherwise); 2D platformer derives it from `facing` (0 facing
   * right, PI facing left); both hold the last value while not moving,
   * matching `Tilemap2DNpcFacing`'s own "stays as last authored/observed
   * while stationary" convention (npc2d.ts) rather than snapping to a
   * default.
   */
  onPlayerTransform?: (transform: { x: number; y: number; z: number; yaw: number }) => void;
  /** Show a 3D grid in the edit viewport (ignored in play mode). */
  showGrid?: boolean;
  /**
   * Reports which scene actually mounted for this render: '2D' when the
   * tilemap2d layer rendered, '3D' when the level rendered, or `null` when a
   * '2D' projection was requested but has no tilemap2d yet, so neither
   * scene rendered. A '2D' projection request does not guarantee a '2D'
   * result here, lets a host's own chrome (control hints, empty-state copy)
   * match what actually mounted instead of assuming the request.
   */
  onActiveProjection?: (active: '2D' | '3D' | null) => void;
  /**
   * The project's authored Logic document (play mode only; ignored in
   * edit mode). Drives a `ClientLogicRuntime` for the duration of this
   * mount: real `trigger-enter`/`trigger-exit` events from the player's own
   * position (3D and 2D both), a one-time `player-join`/`round-start`
   * session lifecycle, and a registered `'teleport'` trigger handler that
   * actually moves the 3D player. Omitted defaults to an empty document
   * (zero rules) - trigger dispatch (teleport) still runs even with no
   * authored Logic rules, since it does not depend on the document.
   */
  logicDocument?: LogicDocument;
  /** Called for every Logic effect the moment it is produced (announce,
   * emit, item pickups) - a host wires this to its own HUD feed. */
  onLogicEffect?: (effect: LogicEffect) => void;
  /** Handed the live `ClientLogicRuntime` once play mode mounts it, so a
   * host can read live variable/item state (health, ammo, inventory) each
   * frame without GameRuntime re-rendering on every Logic tick. Set back to
   * `null` on unmount. */
  logicRuntimeRef?: MutableRefObject<ClientLogicRuntimeHandle | null>;
  /**
   * The same running session's concrete `ClientLogicRuntime` (not just the
   * read-only `ClientLogicRuntimeHandle` shape `logicRuntimeRef` exposes),
   * for a caller that needs to *write* through it - specifically the
   * HollowScript scripting console's `variables`/`logic` capabilities
   * (`setVariable`/`applyAction`), which are deliberately not on the public
   * host-facing handle. Set alongside `logicRuntimeRef` to the exact same
   * object; kept as a separate prop rather than widening
   * `ClientLogicRuntimeHandle` itself so an ordinary host reading live
   * variable/item state still only ever sees a read-only-shaped ref.
   */
  scriptLogicRef?: MutableRefObject<ClientLogicRuntime | null>;
  /**
   * The project's authored cutscene library (play mode, 3D only - the same
   * "no 2D equivalent yet" scope as `externalTeleportRef`/teleport
   * triggers). The first authored cutscene, if any, plays once when this
   * mount's play session starts - the "opening cinematic" convention until
   * a real per-cutscene trigger exists (e.g. a future Logic action).
   */
  cutscenes?: Cutscene[];
  /** Called for each audio cue an active cutscene's audio track fires -
   * the host resolves what a cue name means, mirroring `onLogicEffect`'s
   * "report it, the host decides" contract. */
  onCutsceneCue?: (cue: string) => void;
  /** Called for each signal event an active cutscene's signal track fires -
   * the host's own event/Logic system decides what it does. */
  onCutsceneSignal?: (event: string) => void;
  /**
   * Platformer physics tuning for the 2D platformer branch (2D projection,
   * `Tilemap2D.mode === 'platformer'`) - e.g. Game Setup's `gravity` field,
   * previously saved and never read by any runtime. Merged over
   * `DEFAULT_PLATFORMER_CONFIG`; omitted fields keep their default. Ignored
   * outside that branch (top-down 2D and 3D have no platformer physics).
   */
  platformerConfig?: Partial<PlatformerConfig>;
  /**
   * The real relay-issued player id for this session's local player
   * (play mode only; ignored in edit mode) - a host reads this from the
   * multiplayer relay connection (`playerSession.ts`'s cached guest id) once
   * it is open, and omits it otherwise (single-player/no-relay). Threaded
   * straight through to `ClientLogicRuntime`'s `localPlayerId`, kept live
   * via `setLocalPlayerId` rather than recreating the runtime, so a relay
   * connection opening mid-session does not reset already-evaluated Logic
   * state. See `ClientLogicRuntimeOptions.localPlayerId`'s own doc comment.
   */
  localPlayerId?: string;
  /**
   * The real connected-player count from relay presence (play mode only;
   * ignored in edit mode), when a relay connection exists. Omitted defaults
   * to the single-player value inside `ClientLogicRuntime`. Threaded through
   * to `ClientLogicRuntime`'s `playerCount`, kept live via `setPlayerCount`
   * as presence changes over the session rather than recreating the
   * runtime. See `ClientLogicRuntimeOptions.playerCount`'s own doc comment.
   */
  playerCount?: number;
  /**
   * Whether this session's local player is the elected Logic host (play
   * mode only; ignored in edit mode) - roadmap section 3.2, computed by a
   * host from relay presence via `electHost`/`isElectedHost`
   * (`src/engine/runtime/multiplayer/hostElection.ts`). Omitted defaults to
   * `true` inside `ClientLogicRuntime` (single-player/no-relay safe).
   * Threaded through to `ClientLogicRuntime`'s `isHost`, kept live via
   * `setIsHost` as presence changes (including a host migration) rather
   * than recreating the runtime - same pattern as `localPlayerId`/
   * `playerCount`. See `ClientLogicRuntimeOptions.isHost`'s own doc
   * comment for what this actually gates.
   */
  isHost?: boolean;
  /** Host-only: called for each effect a shared-lifecycle Logic event
   * produces (roadmap 3.2/3.4) - a host wires this to broadcast a
   * `logic-effect` message over the relay. See
   * `ClientLogicRuntimeOptions.onSharedEffect`'s own doc comment; passed
   * straight through at construction, same as `onLogicEffect`. */
  onSharedEffect?: (effect: LogicEffect) => void;
  /**
   * Every currently-connected remote player's id (play mode only; relay
   * presence minus the local player) and the shared `Interpolator` their
   * transforms are buffered into (roadmap Phase 4) - rendered as one
   * avatar/sprite per id, positioned from `remoteInterpolator`'s own
   * per-frame sampling rather than any authored placement. Both omitted
   * (the common case: no relay, or relay not yet connected) renders no
   * remote players at all, the same "additive, no-op until wired" posture
   * presence already has elsewhere in this runtime.
   */
  remotePlayerIds?: readonly string[];
  remoteInterpolator?: Interpolator<InterpolatedTransform>;
  /**
   * The project roster entry (`characters`) chosen as the player's own body
   * (Game Setup's "Player character" picker) - matched by id against
   * `characters`, resolved into a `PlayerAvatarCustomBody` and passed to
   * both the local `PlayerAvatar` and every `RemotePlayers` avatar, so every
   * client in a match renders the same chosen body. Omitted, a dangling id,
   * or a match with no `importedGlbUrl` (a bone-gizmo-only entry is not a
   * viewable close-up first-person body) all fall back to the stock
   * prototype rig - see `resolvePlayerCharacter`.
   */
  playerCharacterId?: string;
}

const EMPTY_LOGIC_DOCUMENT: LogicDocument = { version: 2, rules: [] };

const DEFAULT_DIRECTIONAL: NonNullable<SceneTheme['directional']> = {
  intensity: 1.1,
  position: [6, 12, 8],
  castShadow: true,
};

// Zeroes 2D top-down movement input outright - `Tilemap2DScene`'s own
// `beforeMovementResolve` extension point ("a chance to return an override,
// e.g. zero it while some scene-owned system says movement is locked", its
// own doc comment), reused here for a resolved match outcome rather than
// inventing a second lock. Module-level so it is referentially stable
// (matters less for a prop read fresh every frame, but costs nothing).
function lockTopDownMovement(): { x: number; y: number } {
  return { x: 0, y: 0 };
}

/** What `ClientLogicRuntime.setIsHost` should actually be called with, given
 * this render's `isHost` prop - pulled out as its own pure function (rather
 * than inlined in the isHost-sync effect) so this correctness-critical
 * decision has a direct unit test, same reasoning as `useMultiplayerSync.ts`'s
 * own `resolveIsHost`.
 *
 * `undefined` (no relay concept at all, or a relay connection that has
 * permanently given up rather than merely not being open/elected yet - see
 * `useMultiplayerSync.ts`'s `resolveIsHost` and `useRelayConnection.ts`'s
 * `'failed'` status) must resolve to `true`, matching
 * `ClientLogicRuntimeOptions.isHost`'s own single-player-safe default
 * exactly - never left un-set, which is what previously let a client's
 * `isHost` get stuck at a stale `false` forever once its caller's own
 * resolved value moved on from "not yet elected" to "no relay to elect
 * against at all". */
export function resolveLogicRuntimeIsHost(isHost: boolean | undefined): boolean {
  return isHost ?? true;
}

/**
 * Resolves `GameRuntimeProps.playerCharacterId` against the project's own
 * roster/pose libraries into a `PlayerAvatarCustomBody`, or `null` to fall
 * back to the stock prototype rig. Pulled out as a pure function (same
 * "extract for direct testability" convention as `resolveLogicRuntimeIsHost`
 * above) since the fail-soft cases here matter: an unset or dangling id, or
 * a roster entry with no `importedGlbUrl` (a flat bone-transform rig with no
 * imported mesh is not a viewable close-up first-person body), must all
 * resolve to `null` rather than throw or render nothing. Reuses
 * `resolveIdlePose`/`resolveWalkPose` (`PlacedCharacters.tsx`) so pose
 * resolution for the player's own body matches every placed NPC's exactly.
 */
export function resolvePlayerCharacter(
  characters: CharacterDefinition[] | undefined,
  poseLibraries: CharacterPoseLibrary[] | undefined,
  playerCharacterId: string | undefined,
): PlayerAvatarCustomBody | null {
  if (!playerCharacterId) return null;
  const definition = characters?.find((c) => c.id === playerCharacterId);
  if (!definition || !definition.importedGlbUrl) return null;
  const poses = (poseLibraries ?? [])
    .filter((lib) => lib.rigId === definition.rigId)
    .flatMap((lib) => lib.poses);
  const idlePose = resolveIdlePose(definition, poses);
  const walkPose = resolveWalkPose(poses, idlePose);
  return {
    importedGlbUrl: definition.importedGlbUrl,
    boneMapping: definition.boneMapping,
    idlePose,
    walkPose,
  };
}

/**
 * Generic game runtime: a `<Canvas>` with theme-driven lighting and fog, the
 * registry-driven `LevelRenderer`, a first-person controller derived from the
 * MapDocument, optional post-processing, and - in `edit` mode - the
 * `MapEditorOverlay`. Takes `{ mapDocument, config, characters, mode }` and
 * depends on no game code. Games and the Studio both mount this.
 */
export function GameRuntime({
  mapDocument,
  config,
  characters,
  poseLibraries,
  mode,
  projection = '3D',
  chrome = 'full',
  roomResolver,
  editorTitle,
  runtimeState,
  callbacks,
  onPlayerPosition,
  onPlayerTransform,
  showGrid = false,
  onActiveProjection,
  logicDocument,
  onLogicEffect,
  logicRuntimeRef,
  scriptLogicRef,
  cutscenes,
  onCutsceneCue,
  onCutsceneSignal,
  platformerConfig,
  localPlayerId,
  playerCount,
  isHost,
  onSharedEffect,
  remotePlayerIds,
  remoteInterpolator,
  playerCharacterId,
}: GameRuntimeProps) {
  const fov = useEngineRuntimeStore((state) => state.fov);
  const currentFloor = useEngineRuntimeStore((state) => state.currentFloor);
  const [, setLocked] = useState(false);
  const motionStateRef = useRef<PlayerMotionState | null>(null);
  const externalTeleportRef = useRef<TeleportRequest | null>(null);
  // 2D top-down has no rotation concept of its own (a flat sprite only
  // flips left/right - see Tilemap2DNpcFacing) - `onPlayerTransform`'s yaw
  // is derived from movement direction instead, held at its last value
  // while stationary rather than snapping to 0 (matching npc2d.ts's own
  // "stays as last authored/observed while not moving" convention).
  const lastTopDownYawRef = useRef(0);
  const cutsceneCameraOverrideRef = useRef<CameraOverrideTarget | null>(null);
  // The shared, mutable actor registry a cutscene's actor track drives:
  // `PlacedCharacter` (LevelRenderer) registers itself here on mount (see
  // its own doc comment) and `CutscenePlayer` below reads the very same
  // object every frame. Mutated in place by registrations, not replaced, so
  // CutscenePlayer sees new/removed actors without GameRuntime needing to
  // re-render for it.
  const cutsceneActorsRef = useRef<Record<string, CutsceneActor>>({});
  // The project's opening cinematic, if authored - plays once per play
  // session, the same one-time lifecycle `ClientLogicRuntime` already gives
  // `player-join`/`round-start` below. Cleared on completion so control
  // hands back to the player (CutscenePlayer unmounts, which is what
  // actually releases `cutsceneCameraOverrideRef` - see its own doc
  // comment on why a mid-session `onComplete` alone can't do that).
  const [activeCutscene, setActiveCutscene] = useState<Cutscene | null>(null);
  useEffect(() => {
    setActiveCutscene(mode === 'play' ? cutscenes?.[0] ?? null : null);
  }, [mode, cutscenes]);

  const mapData = useMemo(
    () => deriveMapData(mapDocument, config.runtime),
    [mapDocument, config.runtime],
  );

  const playerCustomBody = useMemo(
    () => resolvePlayerCharacter(characters, poseLibraries, playerCharacterId),
    [characters, poseLibraries, playerCharacterId],
  );

  // The 2D layer's own trigger volumes, converted to the shared
  // MapTriggerVolume shape `diffTriggerContainment`/`ClientLogicRuntime.tick`
  // already operate on (see `tilemap2DTriggersToMapTriggerVolumes`).
  // Computed unconditionally (mapDocument may not even be a 2D project) so
  // this hook stays above every early return below, same as `mapData`.
  const twoDTriggers = useMemo<MapTriggerVolume[]>(
    () => tilemap2DTriggersToMapTriggerVolumes(mapDocument.tilemap2d?.triggers ?? []),
    [mapDocument.tilemap2d?.triggers],
  );

  // Merged once here rather than inside Platformer2DScene, so a host that
  // never passes `platformerConfig` (every call site but the two that read
  // Game Setup's gravity field) sees byte-identical behaviour to before this
  // prop existed.
  const mergedPlatformerConfig = useMemo<PlatformerConfig>(
    () => ({ ...DEFAULT_PLATFORMER_CONFIG, ...platformerConfig }),
    [platformerConfig],
  );

  // ── Logic runtime (play mode only) ─────────────────────────────────────
  // A fresh runtime per mount/document, matching every other per-session
  // piece of state here (motionStateRef, the 2D player's own local state).
  // Recreated whenever the authored document identity changes (Studio's
  // useModeDocument hands back a new object on save, not a mutated one), so
  // a creator editing Logic mid-preview does not carry stale rule state
  // forward across saves.
  const logicRuntime = useMemo(
    () =>
      mode === 'play'
        ? new ClientLogicRuntime({
            document: logicDocument ?? EMPTY_LOGIC_DOCUMENT,
            onEffect: onLogicEffect,
            onSharedEffect,
            localPlayerId,
            playerCount,
            isHost,
          })
        : null,
    // localPlayerId/playerCount/isHost deliberately excluded: they can
    // change over a single play session (a relay connection opening after
    // mount, presence changing as players join/leave, a host migration) and
    // must not reset the runtime's own evaluated state (variables, quests,
    // once-fired rules) when they do - see the three effects below, which
    // keep them live instead. onSharedEffect is not kept live (same as
    // onLogicEffect/onEffect already isn't) - a caller's callback identity
    // is expected to stay stable for a mount's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, logicDocument],
  );
  useEffect(() => {
    if (logicRuntimeRef) logicRuntimeRef.current = logicRuntime;
    if (scriptLogicRef) scriptLogicRef.current = logicRuntime;
    return () => {
      if (logicRuntimeRef) logicRuntimeRef.current = null;
      if (scriptLogicRef) scriptLogicRef.current = null;
    };
  }, [logicRuntime, logicRuntimeRef, scriptLogicRef]);
  // Keeps the runtime's local-player id current as a relay connection opens
  // (or reconnects with a fresh identity) after this mount's initial render
  // - see `localPlayerId`'s own prop doc comment for why this stays a live
  // update rather than a recreation dependency above.
  useEffect(() => {
    if (!logicRuntime || !localPlayerId) return;
    logicRuntime.setLocalPlayerId(localPlayerId);
  }, [logicRuntime, localPlayerId]);
  // Keeps the runtime's connected-player count current as relay presence
  // changes over the session - same reasoning as the effect above.
  useEffect(() => {
    if (!logicRuntime || playerCount === undefined) return;
    logicRuntime.setPlayerCount(playerCount);
  }, [logicRuntime, playerCount]);
  // Keeps the runtime's host status current as election's result changes
  // (presence changing, or a host migration) - same reasoning as the two
  // effects above.
  //
  // `isHost` transitions between three states over a real session's
  // lifetime, not just two: `undefined` (edit mode, or no relay concept at
  // all - see `isHost`'s own prop doc comment), `false` (a relay connection
  // is expected but not yet open/elected, or genuinely lost election), and
  // `true` (elected host). This effect must apply all three, every time -
  // including a transition *back* to `undefined` after having been a real
  // boolean (e.g. `useMultiplayerSync`'s own `resolveIsHost` falling back to
  // its single-player-safe default once a relay connection has permanently
  // given up retrying, see `useRelayConnection.ts`'s `'failed'` status).
  // Skipping the update on `undefined` (as this used to) is exactly what let
  // a client get stuck reporting `isHost: false` forever in that case: the
  // caller's own resolved value moved on to "no relay, single-player-safe
  // default", but this effect never told `logicRuntime` because it only
  // ever forwarded a real boolean. `?? true` mirrors
  // `ClientLogicRuntimeOptions.isHost`'s own single-player-safe default
  // exactly, so `undefined` here always means the same thing `omitting isHost
  // entirely at construction` already means.
  useEffect(() => {
    if (!logicRuntime) return;
    logicRuntime.setIsHost(resolveLogicRuntimeIsHost(isHost));
  }, [logicRuntime, isHost]);

  // ── Match outcome (Logic win/lose) ──────────────────────────────────────
  // `LogicEvaluator.outcome()` is already computed and reaches the HUD as a
  // text feed line via `onLogicEffect` (see `useHudPlayState`'s own
  // 'win'/'lose' formatting) - this is the other half: locking further
  // gameplay input the moment a match actually ends. Checked once per frame
  // right alongside every `logicRuntime.tick()` call below (3D's `onTick`,
  // both 2D scenes' own per-frame callback), not on the HUD's own slower
  // poll, so input stops the same frame the outcome resolves rather than up
  // to 250ms later. `matchOutcomeRef` is the fast per-frame "already
  // resolved, stop checking" guard; `matchOutcome` (state) is what actually
  // reaches the 2D scenes' own input-lock props below and drives the 3D
  // `paused` effect just after it - both need to be reactive, unlike the
  // ref alone.
  const [matchOutcome, setMatchOutcome] = useState<{ result: 'win' | 'lose'; team?: string } | null>(null);
  const matchOutcomeRef = useRef<typeof matchOutcome>(null);
  useEffect(() => {
    matchOutcomeRef.current = null;
    setMatchOutcome(null);
  }, [logicRuntime]);

  // 3D input gating reuses `FirstPersonController`'s own `paused` engine-
  // store flag (see its doc comment: "consumers freeze simulation... while
  // this is set") rather than inventing a second lock - it already releases
  // pointer lock and blocks mouse-look while set; `FirstPersonController`
  // now also gates WASD/interact/primary-action on it (see its own onKeyDown
  // and movement-assembly changes). Reset to false on mount/unmount and
  // whenever a fresh session starts, since the store is a module-level
  // singleton, not scoped per `GameRuntime` mount - without this a finished
  // session's lock would leak into the next one (e.g. re-entering Preview).
  useEffect(() => {
    if (mode !== 'play') return;
    useEngineRuntimeStore.getState().setPaused(false);
    return () => useEngineRuntimeStore.getState().setPaused(false);
  }, [mode, logicRuntime]);
  useEffect(() => {
    if (matchOutcome) useEngineRuntimeStore.getState().setPaused(true);
  }, [matchOutcome]);

  // Called alongside every `logicRuntime.tick()` below (3D's `onTick`, both
  // 2D scenes' own per-frame callback) - a plain function rather than a
  // `useCallback`, since none of its three call sites need referential
  // stability (`playerCallbacks` already stores its own callbacks in a ref
  // inside FirstPersonController; the 2D branch's callbacks are already
  // recreated fresh every render, same as `onFrameResolved` already is).
  function checkMatchOutcome(): void {
    if (matchOutcomeRef.current || !logicRuntime) return;
    const result = logicRuntime.outcome();
    if (result) {
      matchOutcomeRef.current = result;
      setMatchOutcome(result);
    }
  }

  // ── Live scene store (play mode only) ──────────────────────────────────
  // Seeds the live, mutable object-instance store from the authored
  // document once per play session/mount, so `LevelRenderer` has something
  // to read from in play mode. Reset on unmount rather than left to carry
  // state into the next session, since the store is a module-level
  // singleton, not scoped per `GameRuntime` mount.
  //
  // Deliberately keyed on `mode` alone, not `mapDocument.objectInstances`:
  // a `mapDocument` reference change mid-session does not mean the
  // authored content actually changed - Studio's own editor store
  // (`mapEditorStore`) replaces `objectInstances` with a freshly-cloned
  // array on every save response, including an autosave a mode switch can
  // still have in flight when Preview mounts. Reseeding on every such
  // reference churn would silently wipe anything a running script had
  // spawned/moved/removed via the `entities` capability, mid-session, with
  // no error - `mode` never changes for the lifetime of a single mount (a
  // route change remounts this component entirely), so seeding once here
  // is already "once per play session," the same guarantee the comment
  // above claims, just without a dependency that can churn independently
  // of it.
  useEffect(() => {
    if (mode !== 'play') return;
    useLiveSceneStore.getState().seed(mapDocument.objectInstances);
    return () => resetLiveSceneStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above: seeding must happen exactly once per mount, not on every mapDocument reference change.
  }, [mode]);

  // A registered 'teleport' handler is the mechanical half of a teleport
  // trigger (see triggerRegistry.ts's own doc comment: engine defaults are
  // expected to be overridable by a game's own registration). Re-registered
  // whenever the runtime instance changes, so the handler's closure never
  // targets a stale runtime after a document reload; 3D-only for now (see
  // externalTeleportRef's consumer, FirstPersonController) - a 2D
  // equivalent is real, separate follow-up work, not part of this pass.
  useEffect(() => {
    if (!logicRuntime) return;
    registerTriggerHandler('teleport', (volume, ctx) => {
      externalTeleportRef.current = {
        floor: volume.targetFloor ?? ctx.playerFloor,
        x: volume.targetPosition?.[0] ?? ctx.playerX,
        z: volume.targetPosition?.[1] ?? ctx.playerZ,
      };
    });
    return () => unregisterTriggerHandler('teleport');
  }, [logicRuntime]);

  const playerCallbacks = useMemo<PlayerCallbacks>(
    () => ({
      onLockChange: (locked) => {
        setLocked(locked);
        callbacks?.onLockChange?.(locked);
      },
      onLockError: callbacks?.onLockError,
      onTick: (dt, motion) => {
        onPlayerPosition?.(motion.x, motion.y, motion.z);
        onPlayerTransform?.({ x: motion.x, y: motion.y, z: motion.z, yaw: motion.yaw });
        logicRuntime?.tick({ x: motion.x, z: motion.z, floor: motion.floor }, mapDocument.triggers ?? []);
        // Inlined rather than calling the shared `checkMatchOutcome` below -
        // that's a plain function redefined every render, so referencing it
        // here would make this memo depend on it too, invalidating the memo
        // every render for no real benefit (`matchOutcomeRef`/`setMatchOutcome`
        // are already stable; `logicRuntime` is already a dep below).
        if (!matchOutcomeRef.current && logicRuntime) {
          const result = logicRuntime.outcome();
          if (result) {
            matchOutcomeRef.current = result;
            setMatchOutcome(result);
          }
        }
        callbacks?.onTick?.(dt, motion);
      },
      onInteract: callbacks?.onInteract,
      onPrimaryAction: callbacks?.onPrimaryAction,
      onFootstep: callbacks?.onFootstep,
      onStairTransition: callbacks?.onStairTransition,
    }),
    [callbacks, onPlayerPosition, onPlayerTransform, logicRuntime, mapDocument.triggers],
  );

  const theme = config.theme;
  const directional = theme.directional ?? DEFAULT_DIRECTIONAL;
  const isEdit = mode === 'edit';

  // Theme-authored post-processing amounts drive the runtime store the
  // PostEffects stack reads; absent values fall back to the engine baseline.
  useEffect(() => {
    const state = useEngineRuntimeStore.getState();
    state.setBloomIntensity(theme.bloom ?? DEFAULT_BLOOM_INTENSITY);
    state.setVignetteAmount(theme.vignette ?? DEFAULT_VIGNETTE_AMOUNT);
  }, [theme.bloom, theme.vignette]);
  const twoD = projection === '2D' && mapDocument.tilemap2d ? mapDocument.tilemap2d : null;
  // A '2D' request with no tilemap2d layer yet has nothing 2D to render;
  // previously this fell through to the full 3D branch below, silently
  // handing a 2D project a first-person 3D scene with no indication that
  // had happened.
  const missingTilemap = projection === '2D' && !twoD;

  useEffect(() => {
    onActiveProjection?.(missingTilemap ? null : twoD ? '2D' : '3D');
  }, [missingTilemap, twoD, onActiveProjection]);

  if (missingTilemap) {
    return (
      <div
        id="app-shell"
        className="game-runtime-shell"
        style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: theme.background, padding: 24,
        }}
      >
        <div style={{ maxWidth: 360, textAlign: 'center', fontFamily: 'inherit' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.92)', marginBottom: 8 }}>
            No 2D tilemap yet
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'rgba(255,255,255,0.68)' }}>
            This project is set to 2D, but no tilemap has been saved. Open Pixel Studio and click &ldquo;Save to assets&rdquo; to build a playable 2D scene.
          </div>
        </div>
      </div>
    );
  }

  if (twoD) {
    // 2D has no floor concept (a single flat plane), so every 2D trigger
    // volume ticks at floor 0 (see `twoDTriggers` above, which converts
    // `Tilemap2D.triggers` to that shape) - real containment now, not the
    // permanently-empty array this used to pass. Both 2D modes tick the
    // same runtime from their own per-frame hook (Tilemap2DScene's
    // onFrameResolved, Platformer2DScene's onPlayerStateChange - the only
    // per-frame position callback it has), so a Logic rule fires
    // identically regardless of which mode a project uses.
    const onFrameResolved = logicRuntime || onPlayerTransform
      ? (info: Tilemap2DFrameInfo) => {
          if (onPlayerTransform) {
            if (info.velocity.x !== 0 || info.velocity.y !== 0) {
              lastTopDownYawRef.current = Math.atan2(info.velocity.x, info.velocity.y);
            }
            onPlayerTransform({ x: info.position.x, y: 0, z: info.position.y, yaw: lastTopDownYawRef.current });
          }
          logicRuntime?.tick({ x: info.position.x, z: info.position.y, floor: 0 }, twoDTriggers);
          checkMatchOutcome();
        }
      : undefined;
    // A resolved match outcome locks further gameplay input, both 2D modes,
    // via each scene's own input-lock extension point - see
    // lockTopDownMovement's own doc comment for Tilemap2DScene's
    // `beforeMovementResolve`; Platformer2DScene's `movementLocked` is the
    // same idea applied directly where all of that scene's own input
    // already funnels through before physics ever sees it.
    const inputLocked = matchOutcome !== null;
    return (
      <div id="app-shell" className="game-runtime-shell" style={{ position: 'absolute', inset: 0 }}>
        <Viewport gl={{ antialias: false }}>
          <color attach="background" args={[theme.background]} />
          {twoD.mode === 'platformer' ? (
            <>
              <Platformer2DScene
                map={twoD}
                mode={mode}
                config={mergedPlatformerConfig}
                movementLocked={inputLocked}
                onPlayerStateChange={(state) => {
                  onPlayerPosition?.(state.x, 0, state.y);
                  onPlayerTransform?.({ x: state.x, y: 0, z: state.y, yaw: state.facing === 'left' ? Math.PI : 0 });
                  logicRuntime?.tick({ x: state.x, z: state.y, floor: 0 }, twoDTriggers);
                  checkMatchOutcome();
                }}
              />
              {mode === 'play' && remotePlayerIds && remoteInterpolator ? (
                <RemotePlayers2D ids={remotePlayerIds} interpolator={remoteInterpolator} sprite={twoD.playerSprite} />
              ) : null}
            </>
          ) : (
            <>
              <Tilemap2DScene
                map={twoD}
                mode={mode}
                onPlayerMove={onPlayerPosition ? (x, y) => onPlayerPosition(x, 0, y) : undefined}
                beforeMovementResolve={inputLocked ? lockTopDownMovement : undefined}
                onFrameResolved={onFrameResolved}
              />
              {mode === 'play' && remotePlayerIds && remoteInterpolator ? (
                <RemotePlayers2D ids={remotePlayerIds} interpolator={remoteInterpolator} sprite={twoD.playerSprite} />
              ) : null}
            </>
          )}
        </Viewport>
        {mode === 'play' ? <TouchControlsOverlay projection="2D" /> : null}
      </div>
    );
  }

  return (
    <div
      id="app-shell"
      className="game-runtime-shell"
      style={{ position: 'absolute', inset: 0 }}
      onDragOver={isEdit ? (event) => event.preventDefault() : undefined}
      onDrop={
        isEdit
          ? (event) => {
              event.preventDefault();
              const store = useMapEditorStore.getState();
              const assetId = event.dataTransfer.getData('text/plain') || store.pendingAssetId;
              if (!assetId) return;
              const placement = resolveDropPlacement(event.clientX, event.clientY);
              if (!placement) return;
              store.addObject(assetId, placement.floor as MapFloorLevel, placement.point);
            }
          : undefined
      }
    >
      <Viewport shadows gl={{ antialias: true }}>
        <PerspectiveCamera makeDefault fov={fov} near={0.05} far={200} position={mapData.spawn.position} />
        <color attach="background" args={[theme.background]} />
        {theme.fog ? (
          <fog attach="fog" args={[theme.fog.color, theme.fog.near, theme.fog.far]} />
        ) : null}

        {/* onPointerMissed is scoped here (rather than on the old
            Canvas-level prop it replaces) since a shared, host-owned canvas
            has no single Canvas element of this route's own to hang it on -
            any always-present scene object works identically, since R3F
            dispatches onPointerMissed to every registered handler in the
            view when a click hits nothing, not just the object it's on. */}
        <ambientLight
          color={theme.ambient.color}
          intensity={theme.ambient.intensity}
          onPointerMissed={
            isEdit
              ? () => {
                  const { pendingAssetId } = useMapEditorStore.getState();
                  if (pendingAssetId) {
                    useMapEditorStore.setState({ statusMessage: 'Aim at the floor to place - that click missed the level geometry.' });
                  }
                }
              : undefined
          }
        />
        {theme.hemisphere ? (
          <hemisphereLight
            args={[theme.hemisphere.sky, theme.hemisphere.ground, theme.hemisphere.intensity]}
          />
        ) : null}
        <directionalLight
          color={directional.color}
          intensity={directional.intensity}
          position={directional.position}
          castShadow={directional.castShadow ?? true}
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />

        <Suspense fallback={null}>
          <LevelRenderer
            mapDocument={mapDocument}
            mode={mode}
            useLiveScene={mode === 'play'}
            runtimeOptions={config.runtime}
            runtimeState={runtimeState}
            characters={characters}
            poseLibraries={poseLibraries}
            cutsceneActorsRef={cutsceneActorsRef}
          />
        </Suspense>

        <FirstPersonController
          config={config.player}
          mapData={mapData}
          callbacks={playerCallbacks}
          motionStateRef={motionStateRef}
          externalTeleportRef={externalTeleportRef}
          externalCameraOverrideRef={cutsceneCameraOverrideRef}
        >
          <PlayerAvatar motionStateRef={motionStateRef} config={config.player} customBody={playerCustomBody ?? undefined} />
        </FirstPersonController>

        {mode === 'play' && remotePlayerIds && remoteInterpolator ? (
          <RemotePlayers
            ids={remotePlayerIds}
            interpolator={remoteInterpolator}
            config={config.player}
            customBody={playerCustomBody ?? undefined}
          />
        ) : null}

        {mode === 'play' && activeCutscene ? (
          <CutscenePlayer
            cutscene={activeCutscene}
            actors={cutsceneActorsRef.current}
            cameraOverrideRef={cutsceneCameraOverrideRef}
            onCue={onCutsceneCue}
            onSignal={onCutsceneSignal}
            onPost={(post) => {
              // CutscenePlayer's own contract: post values are absolute,
              // not deltas, so the caller (here) owns restoring the scene's
              // own baseline - done in onComplete below, not by this
              // component itself.
              const store = useEngineRuntimeStore.getState();
              if (post.bloomIntensity !== null) store.setBloomIntensity(post.bloomIntensity);
              if (post.vignetteAmount !== null) store.setVignetteAmount(post.vignetteAmount);
            }}
            onComplete={() => {
              setActiveCutscene(null);
              const store = useEngineRuntimeStore.getState();
              store.setBloomIntensity(theme.bloom ?? DEFAULT_BLOOM_INTENSITY);
              store.setVignetteAmount(theme.vignette ?? DEFAULT_VIGNETTE_AMOUNT);
            }}
          />
        ) : null}

        {theme.postEffects ? <PostEffects /> : null}

        {isEdit && showGrid ? (
          <Grid
            position={[0, floorWorldY(currentFloor, config.runtime) + 0.005, 0]}
            args={[100, 100]}
            cellSize={1}
            cellThickness={0.4}
            cellColor="#4a525c"
            sectionSize={5}
            sectionThickness={0.8}
            sectionColor="#5a6370"
            fadeDistance={60}
            fadeStrength={1.2}
            infiniteGrid
          />
        ) : null}
      </Viewport>

      {mode === 'edit' && chrome === 'full' ? (
        <MapEditorOverlay roomResolver={roomResolver} builderTitle={editorTitle} />
      ) : null}

      {mode === 'play' ? <TouchControlsOverlay projection="3D" /> : null}
    </div>
  );
}
